import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { FormikProps } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import CustomDataGridV2 from "../../../../../../components/CustomDataGridV2";
import CustomSelectScroll from "../../../../../../components/CustomSelectScroll";
import Text from "../../../../../../components/Text";
import DeleteIcon from "../../../../../../components/app/DeleteIcon";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../../../queries/catalog/use-get-catalog-code";
import { Identified } from "../../../../../../types/sdk-types";
import { MedicalCode } from "../../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../../../utils/utils";
import CardView from "../../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";

interface DiagnosisCodeGridForClaimProps {
  formik: FormikProps<any>;
  diagnosisCodes?: any;
  isFetching: boolean;
  isLoading: boolean;
  readOnly?: string;
}

const DiagnosisCodeGridForClaim = ({
  formik,
  diagnosisCodes,
  isFetching,
  isLoading,
  readOnly,
}: DiagnosisCodeGridForClaimProps) => {
  const [gridSorting] = useState<GridSortItem[]>([]);
  // use debounce
  const [tableDataLoading, setTableDataLoading] = useState<boolean>(false);
  const [gridData, setGridData] = useState<any>([]);

  const showSnackbar = useSnackbar();

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const [searchKey, setSearchKey] = useState<string>("");

  const [input, setInput] = useState<any>("");

  const { data, isFetching: codeFetching } = useGetCataLogCode({
    gridSorting,
    pagination,
    searchKey,
    type: "ICD",
    active: true,
    archive: false,
  });

  // The picked ICD code can come from a page no longer in `data.content` once
  // the list has scrolled, so keep every loaded code by uuid to resolve it.
  const codeByUuid = useRef<Record<string, Identified<MedicalCode>>>({});
  useEffect(() => {
    (data?.data?.content ?? []).forEach((it) => {
      if (it?.uuid) codeByUuid.current[it.uuid] = it;
    });
  }, [data?.data?.content]);

  // Calculate dynamic height based on grid data count
  const dynamicHeight = useMemo(() => {
    const rowHeight = 35; // Height of each row
    const headerHeight = 56; // Approximate header height
    const minHeight = 100; // Minimum height
    const maxHeight = 400; // Maximum height to prevent excessive growth

    const dataCount = gridData?.length || 0;
    const calculatedHeight = headerHeight + dataCount * rowHeight;

    // Return calculated height within bounds
    return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
  }, [gridData]);

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "pointer",
      flex: 0.08,
      disableColumnMenu: true,
      editable: false,
      minWidth: 40,
      headerName: "Pointer",
      renderCell: (params) => {
        const index = params.api.getAllRowIds().indexOf(params.id);
        return `${String.fromCharCode(65 + index)}.`;
      },
    },
    {
      field: "code",
      flex: 0.1,
      minWidth: 3,
      disableColumnMenu: true,
      headerName: "Code",
      renderCell: ({ row }) => {
        return <Text>{row?.code}</Text>;
      },
    },
    {
      field: "description",
      flex: 0.5,
      disableColumnMenu: true,
      headerName: "Details",
    },
    {
      field: "SelectFileds",
      flex: 0.15,
      align: "center",
      disableColumnMenu: true,
      // The "Action" header carried no meaning and sat centred while the icon
      // renders right-aligned, so the label is dropped.
      headerName: "",
      renderCell: ({ row: { uuid } }) => (
        <Box
          sx={{ width: "100%", display: "flex", flexDirection: "row-reverse" }}
        >
          {/* Shared DeleteIcon, so the Diagnosis section matches the delete
              icon used in the Procedure section. Previous markup:

          <ButtonBase
            disabled={readOnly === "true"}
            onClick={() => {...}}
          >
            <Typography fontSize={"18px"} fontWeight={"500"}>
              <DeleteOutlineIcon
                color={readOnly === "true" ? "disabled" : "error"}
                sx={{ cursor: "pointer" }}
              />
            </Typography>
          </ButtonBase> */}
          <DeleteIcon
            disabled={readOnly === "true"}
            onClick={() => {
              setTableDataLoading(true);
              // console.log("button clicked", uuid);
              setGridData((prevItems: any) =>
                prevItems.filter((item: any) => item.uuid !== uuid)
              );
              setTableDataLoading(false);
            }}
          />
        </Box>
      ),
    },
  ]);

  useEffect(() => {
    formik.setFieldValue("diagnisisCodeData", gridData);
  }, [gridData]);

  useEffect(() => {
    if (diagnosisCodes) {
      setGridData(diagnosisCodes);
      // console.log("diagnosisCodes ", diagnosisCodes, "gridData ", gridData);
    }
  }, [diagnosisCodes]);
  // console.log("grid data ", gridData);

  return (
    <CardView
      title="Diagnosis Code"
      titleIcon={
        <ListAltOutlinedIcon sx={{ color: "primary.main" }} fontSize="small" />
      }
      endAction={
        readOnly !== "true" && (
          <Box sx={{ paddingX: "5px" }} width={"30.5vw"}>
            <CustomSelectScroll
              key={`diag-claim-select-${gridData?.length ?? 0}`}
              disablePortal={false}
              loading={codeFetching}
              pagination={pagination}
              setPagination={setPagination}
              totalElements={data?.data?.page?.totalElements}
              onSearch={(_, val) => {
                setSearchKey(val);
                setPagination((p) => ({ ...p, page: 0 }));
              }}
              options={data?.data?.content?.reduce((acc: any[], it) => {
                if (!gridData?.some((item: any) => item?.uuid === it?.uuid)) {
                  acc.push({
                    label: `${it?.code ? it?.code : "--"} ${it?.description}`,
                    value: it?.uuid,
                  });
                }
                return acc;
              }, [])}
              onChange={(v) => {
                setTableDataLoading(true);
                if (gridData?.length >= 12) {
                  showSnackbar({
                    message: "Max 12 diagnosis codes allowed",
                    type: "error",
                  });
                  setTableDataLoading(false);
                  return;
                }
                const curOption =
                  codeByUuid.current[v as string] ??
                  data?.data?.content?.find((it) => it?.uuid === v);
                setInput(v);
                if (curOption) {
                  if (!gridData?.some((item: any) => item?.uuid === v)) {
                    setGridData((prevItems: any) => [...prevItems, curOption]);
                    setInput("");
                    setSearchKey("");
                  } else {
                    showSnackbar({ message: "Duplicate Entry", type: "error" });
                  }
                }
                setTableDataLoading(false);
              }}
              value={input}
              placeholder="Search & select ICD Code"
            />
          </Box>
        )
      }
    >
      <Box id={"Provider Details"}>
        <CustomDataGridV2
          // searchString={""}
          height={`${dynamicHeight}px`}
          loading={tableDataLoading || isLoading || isFetching}
          getRowId={(row) => row?.uuid}
          rows={gridData ?? []}
          columns={COLUMNS}
          hideFooter
          rowHeight={35}
        />
      </Box>
    </CardView>
  );
};

export default DiagnosisCodeGridForClaim;
